import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, Clock, Disc3, ExternalLink, GripVertical, Link2, Loader2, Music, Pause, Play, RefreshCw, Search, SkipBack, SkipForward, Trash2, Unlink, Volume2, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const statusLabels = {
  pending: 'Afventer',
  approved: 'Godkendt',
  rejected: 'Afvist',
  queued: 'I kø',
  played: 'Spillet',
};

function getRequestId(request) {
  return request?.id || request?._id || request?.request_id || request?.rid || '';
}

function formatDuration(ms) {
  if (!ms) return '0:00';
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function formatTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
}

function spotifyError(detail, fallback = 'Spotify-handlingen mislykkedes.') {
  const text = typeof detail === 'string' ? detail : '';
  if (text.includes('Premium')) return 'Spotify Premium kræves for fjernstyring.';
  if (text.includes('device') || text.includes('playback') || text.includes('enhed')) return 'Åbn Spotify på en enhed og start afspilning.';
  if (text.includes('expired') || text.includes('udløbet') || text.includes('401')) return 'Spotify-forbindelsen er udløbet. Forbind igen.';
  return text || fallback;
}

export default function AdminMusic() {
  const { toast, dismiss } = useToast();
  const [requests, setRequests] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spotifyStatus, setSpotifyStatus] = useState(null);
  const [showSpotify, setShowSpotify] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('all');
  const [volume, setVolume] = useState(50);
  const [dragId, setDragId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const spotifyToastIdRef = useRef(null);
  const removedRequestIdsRef = useRef(new Set());

  const clearSpotifyToast = () => {
    if (spotifyToastIdRef.current) {
      dismiss(spotifyToastIdRef.current);
      spotifyToastIdRef.current = null;
    }
  };

  const showSpotifyError = (title, detail, fallback) => {
    clearSpotifyToast();
    const notification = toast({
      title,
      description: spotifyError(detail, fallback),
      variant: 'destructive',
      duration: 5000,
    });
    spotifyToastIdRef.current = notification.id;
  };

  const load = () => {
    setLoading(true);
    return Promise.all([
      apiGet('/api/music-requests').then(r => {
        setRequests((r.data.music_requests || []).filter(item => !removedRequestIdsRef.current.has(getRequestId(item))));
      }),
      apiGet('/api/spotify/status').then(r => {
        setSpotifyStatus(r.data);
        if (typeof r.data.volume_percent === 'number') setVolume(r.data.volume_percent);
      }).catch(() => setSpotifyStatus({ enabled: false, configured: false, connected: false })),
      apiGet('/api/spotify/queue').then(r => {
        const incoming = (r.data.queue || []).filter(item => !removedRequestIdsRef.current.has(getRequestId(item)));
        setQueue(current => {
          if (current.length === 0) return incoming;
          const incomingById = new Map(incoming.map(item => [getRequestId(item), item]));
          const ordered = current
            .filter(item => incomingById.has(getRequestId(item)))
            .map(item => incomingById.get(getRequestId(item)));
          const knownIds = new Set(ordered.map(item => getRequestId(item)));
          return [...ordered, ...incoming.filter(item => !knownIds.has(getRequestId(item)))];
        });
      }).catch(() => setQueue([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const connectSpotify = async () => {
    clearSpotifyToast();
    try {
      const res = await apiGet('/api/spotify/auth-url');
      clearSpotifyToast();
      window.location.href = res.data.auth_url;
    } catch (err) {
      showSpotifyError('Kunne ikke starte Spotify-login', err.response?.data?.detail, 'Kunne ikke starte Spotify-login.');
    }
  };

  const disconnectSpotify = async () => {
    if (!window.confirm('Afbryd Spotify-forbindelsen?')) return;
    clearSpotifyToast();
    try {
      await apiPost('/api/spotify/disconnect', {});
      clearSpotifyToast();
      toast({ title: 'Spotify er afbrudt' });
      load();
    } catch (err) {
      showSpotifyError('Kunne ikke afbryde Spotify', err.response?.data?.detail);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await apiPut(`/api/music-requests/${id}`, { status });
      setRequests(current => current.map(item => item.id === id ? { ...item, status } : item));
      setQueue(current => current.map(item => item.id === id ? { ...item, status } : item));
      toast({ title: 'Musikønsket er opdateret', description: `Status: ${statusLabels[status] || status}` });
    } catch (err) {
      toast({ title: 'Kunne ikke opdatere musikønsket', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const removeRequest = async (request, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    const id = getRequestId(request);
    if (!id) {
      toast({
        title: 'Kunne ikke slette musikønsket',
        description: 'Musikønsket mangler et gyldigt id. Opdater siden og prøv igen.',
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }
    if (deletingIds.has(id)) {
      return;
    }
    setDeletingIds(current => new Set(current).add(id));
    const deletePath = `/api/music-requests/${encodeURIComponent(id)}`;
    try {
      await apiDelete(deletePath);
      removedRequestIdsRef.current.add(id);
      setRequests(current => current.filter(item => getRequestId(item) !== id));
      setQueue(current => current.filter(item => getRequestId(item) !== id));
      toast({ title: 'Musikønske slettet', duration: 4000 });
      await load();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Ukendt fejl';
      toast({
        title: 'Kunne ikke slette musikønsket',
        description: message,
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setDeletingIds(current => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDeleteClick = (request, event) => {
    void removeRequest(request, event);
  };

  const handleSpotifySearch = async () => {
    if (!searchQuery.trim()) return;
    clearSpotifyToast();
    setSearching(true);
    try {
      const r = await apiGet(`/api/spotify/search?q=${encodeURIComponent(searchQuery)}`);
      clearSpotifyToast();
      setSearchResults(r.data.tracks || []);
    } catch (err) {
      showSpotifyError('Spotify-søgning mislykkedes', err.response?.data?.detail, 'Spotify-søgning mislykkedes.');
    } finally {
      setSearching(false);
    }
  };

  const handleLinkTrack = async (requestId, track) => {
    clearSpotifyToast();
    try {
      await apiPost(`/api/spotify/link/${requestId}`, { track });
      clearSpotifyToast();
      setShowSpotify(null);
      setSearchQuery('');
      setSearchResults([]);
      load();
    } catch (err) {
      showSpotifyError('Kunne ikke tilknytte nummeret', err.response?.data?.detail, 'Kunne ikke tilknytte nummeret.');
    }
  };

  const handleUnlink = async (requestId) => {
    clearSpotifyToast();
    try {
      await apiPost(`/api/spotify/unlink/${requestId}`, {});
      clearSpotifyToast();
      load();
    } catch (err) {
      showSpotifyError('Kunne ikke fjerne Spotify-linket', err.response?.data?.detail, 'Kunne ikke fjerne Spotify-linket.');
    }
  };

  const playback = async (action, body = {}) => {
    clearSpotifyToast();
    try {
      if (action === 'volume') {
        await apiPut('/api/spotify/player/volume', body);
      } else if (action === 'play-request') {
        await apiPost(`/api/spotify/queue/${body.requestId}/play`, {});
      } else {
        await apiPost(`/api/spotify/player/${action}`, body);
      }
      const feedback = {
        play: 'Afspilning startet',
        pause: 'Afspilning sat på pause',
        next: 'Næste nummer afspilles',
        previous: 'Forrige nummer afspilles',
        volume: 'Lydstyrken er opdateret',
        'play-request': 'Nummeret afspilles nu',
      };
      clearSpotifyToast();
      if (feedback[action]) toast({ title: feedback[action], duration: 4000 });
      load();
    } catch (err) {
      showSpotifyError('Spotify-handlingen mislykkedes', err.response?.data?.detail);
    }
  };

  const reorderQueue = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setQueue(current => {
      const fromIndex = current.findIndex(item => item.id === dragId);
      const toIndex = current.findIndex(item => item.id === targetId);
      if (fromIndex < 0 || toIndex < 0) return current;
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const currentTrack = spotifyStatus?.current_track;
  const device = spotifyStatus?.device;
  const connected = spotifyStatus?.connected;
  const hasActiveDevice = connected && !!device?.id;
  const progressMs = spotifyStatus?.progress_ms || 0;
  const durationMs = currentTrack?.duration_ms || 0;
  const progressPct = durationMs ? Math.min(100, Math.max(0, (progressMs / durationMs) * 100)) : 0;

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    approved: 'bg-emerald-500/10 text-emerald-400',
    rejected: 'bg-red-500/10 text-red-400',
    queued: 'bg-blue-500/10 text-blue-400',
    played: 'bg-purple-500/10 text-purple-400',
  };

  const counts = useMemo(() => {
    const next = { pending: 0, approved: 0, rejected: 0, queued: 0, played: 0 };
    requests.forEach(r => { if (next[r.status] !== undefined) next[r.status]++; });
    return next;
  }, [requests]);

  return (
    <div className="animate-fade-in space-y-6" data-testid="admin-music-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Musikcenter</h1>
          <p className="text-sm text-slate-400 mt-1">{requests.length} musikønsker</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 w-fit ${connected ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-800 text-slate-500 border border-slate-700'}`} data-testid="spotify-status-badge">
            <Disc3 className="w-3.5 h-3.5" />
            {connected ? 'Spotify forbundet' : spotifyStatus?.configured ? 'Spotify afbrudt' : 'Spotify ikke konfigureret'}
          </span>
          {connected ? (
            <Button variant="outline" onClick={disconnectSpotify} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">Afbryd</Button>
          ) : (
            <Button onClick={connectSpotify} disabled={!spotifyStatus?.configured} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white">Forbind Spotify</Button>
          )}
          <Button variant="outline" onClick={load} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-music-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="spotify-now-playing">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr]">
          <div className="bg-slate-950/60 p-5 flex items-center justify-center">
            {currentTrack?.album_art ? (
              <img src={currentTrack.album_art} alt="" className="w-full max-w-[280px] aspect-square rounded-xl object-cover shadow-2xl" />
            ) : (
              <div className="w-full max-w-[280px] aspect-square rounded-xl bg-slate-900 flex items-center justify-center">
                <Disc3 className="w-16 h-16 text-slate-600" />
              </div>
            )}
          </div>
          <div className="p-5 sm:p-6 flex flex-col justify-between gap-6 min-w-0">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-400 mb-3">Afspilles nu</p>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-50 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {currentTrack?.name || 'Intet afspilles'}
              </h2>
              <p className="text-lg text-slate-300 truncate mt-2">{currentTrack?.artist || 'Åbn Spotify på en enhed og start afspilning.'}</p>
              <p className="text-sm text-slate-500 truncate mt-1">{currentTrack?.album || 'Der er ingen aktiv afspilning lige nu.'}</p>
            </div>

            <div>
              <div className="h-2 rounded-full bg-slate-900 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-400" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500 tabular-nums">
                <span>{formatDuration(progressMs)}</span>
                <span>{formatDuration(durationMs)}</span>
              </div>
            </div>

            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button size="lg" variant="outline" onClick={() => playback('previous')} disabled={!hasActiveDevice} className="min-h-12 flex-1 sm:flex-none bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40">
                  <SkipBack className="w-5 h-5" />
                </Button>
                {spotifyStatus?.is_playing ? (
                  <Button size="lg" onClick={() => playback('pause')} disabled={!hasActiveDevice} className="min-h-12 min-w-32 flex-[2] sm:flex-none bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                    <Pause className="w-5 h-5 mr-2" /> Sæt på pause
                  </Button>
                ) : (
                  <Button size="lg" onClick={() => playback('play')} disabled={!hasActiveDevice} className="min-h-12 min-w-32 flex-[2] sm:flex-none bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                    <Play className="w-5 h-5 mr-2" /> Afspil
                  </Button>
                )}
                <Button size="lg" variant="outline" onClick={() => playback('next')} disabled={!hasActiveDevice} className="min-h-12 flex-1 sm:flex-none bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40">
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/40 px-3 py-2">
                  <p className="text-xs text-slate-500">Aktiv enhed</p>
                  <p className="text-sm text-slate-200 truncate">{device ? `${device.name} (${device.type})` : 'Ingen aktiv enhed'}</p>
                </div>
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/40 px-3 py-2">
                  <p className="text-xs text-slate-500">Lydstyrke</p>
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-slate-500" />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      disabled={!hasActiveDevice}
                      onChange={e => setVolume(Number(e.target.value))}
                      onMouseUp={() => playback('volume', { volume_percent: volume })}
                      onTouchEnd={() => playback('volume', { volume_percent: volume })}
                      className="w-full"
                    />
                    <span className="text-xs text-slate-400 w-8">{volume}%</span>
                  </div>
                </div>
              </div>
            </div>
            {!hasActiveDevice && connected && (
              <p className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                Åbn Spotify på en enhed og start afspilning.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">Gæsternes musikkø</h2>
          <span className="text-xs text-slate-500">{queue.length} musikønsker</span>
        </div>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Ingen godkendte musikønsker eller numre i kø.</p>
        ) : (
          <div className="space-y-2">
            {queue.map((req, index) => (
              <div
                key={req.id}
                draggable
                onDragStart={() => setDragId(req.id)}
                onDragOver={(e) => { e.preventDefault(); reorderQueue(req.id); }}
                onDragEnd={() => setDragId(null)}
                className={`bg-slate-900/40 border rounded-xl p-3 flex flex-col lg:flex-row lg:items-center gap-3 ${dragId === req.id ? 'border-blue-500/40' : 'border-slate-700/40'}`}
                data-testid={`queue-row-${req.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <GripVertical className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  <span className="text-xs text-slate-600 tabular-nums w-6">{index + 1}</span>
                  {req.spotify_album_art ? (
                    <img src={req.spotify_album_art} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <Music className="w-6 h-6 text-slate-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{req.spotify_name || req.song_title}</p>
                    <p className="text-xs text-slate-400 truncate">{req.spotify_artist || req.artist || '-'}</p>
                    <p className="text-xs text-slate-500 truncate">Ønsket af {req.guest_name || '-'} kl. {formatTime(req.created_at)}</p>
                  </div>
                </div>
                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center lg:justify-end">
                  <span className={`text-xs px-2 py-1 rounded ${statusColors[req.status] || ''}`}>{statusLabels[req.status] || req.status}</span>
                  <Button size="sm" onClick={() => playback('play-request', { requestId: req.id })} disabled={!hasActiveDevice || !req.spotify_uri} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                    <Play className="w-3.5 h-3.5 mr-1" /> Afspil
                  </Button>
                  {req.spotify_external_url && (
                    <a href={req.spotify_external_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 w-full items-center justify-center gap-1 rounded-md bg-slate-800 px-3 py-2 text-xs text-emerald-400 hover:text-emerald-300 sm:w-auto">
                      Åbn <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, 'played')} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-purple-300 hover:bg-slate-700">
                    Markér som spillet
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => handleDeleteClick(req, event)}
                    disabled={deletingIds.has(getRequestId(req))}
                    className="w-full sm:w-auto bg-slate-800 border-slate-700 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                    aria-label="Slet musikønske"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(counts).map(([s, c]) => (
          <button key={s} onClick={() => setFilter(filter === s ? 'all' : s)}
            className={`text-center p-2.5 rounded-lg border text-xs font-medium transition-colors ${filter === s ? 'bg-slate-800 border-blue-500/30 text-blue-400' : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:border-slate-600'}`}
            data-testid={`filter-${s}`}>
            <p className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{c}</p>
            <p className="mt-0.5">{statusLabels[s] || s}</p>
          </button>
        ))}
      </div>

      {loading ? <p className="text-slate-400">Indlæser...</p> : (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="music-requests-table">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Sang</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Ønsket af</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Spotify</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(req => (
                  <tr key={req.id} className="border-b border-slate-700/20 hover:bg-slate-800/30" data-testid={`music-row-${req.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {req.spotify_album_art ? (
                          <img src={req.spotify_album_art} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                            <Music className="w-4 h-4 text-slate-500" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-slate-200 truncate">{req.spotify_name || req.song_title}</p>
                          <p className="text-xs text-slate-500 truncate">{req.spotify_artist || req.artist || '-'}</p>
                          {req.spotify_duration_ms && <p className="text-xs text-slate-600">{formatDuration(req.spotify_duration_ms)}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{req.guest_name}</td>
                    <td className="px-4 py-3">
                      {req.spotify_track_id ? (
                        <div className="flex items-center gap-2">
                          <a href={req.spotify_external_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid={`spotify-link-${req.id}`}>
                            <Disc3 className="w-3.5 h-3.5" /> Tilknyttet <ExternalLink className="w-3 h-3" />
                          </a>
                          <button onClick={() => handleUnlink(req.id)} className="p-1 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400" title="Fjern tilknytning" data-testid={`unlink-${req.id}`}>
                            <Unlink className="w-3 h-3" />
                          </button>
                        </div>
                      ) : spotifyStatus?.app_connected ? (
                        <button onClick={() => { setShowSpotify(req.id); setSearchQuery(req.song_title + (req.artist ? ` ${req.artist}` : '')); }}
                          className="text-xs text-slate-500 hover:text-blue-400 flex items-center gap-1 transition-colors" data-testid={`search-spotify-${req.id}`}>
                          <Link2 className="w-3 h-3" /> Find på Spotify
                        </button>
                      ) : (
                        <span className="text-xs text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${statusColors[req.status] || ''}`}>{statusLabels[req.status] || req.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {req.status === 'pending' && (
                          <>
                            <button onClick={() => updateStatus(req.id, 'approved')} className="p-1.5 rounded hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-400" title="Godkend" data-testid={`approve-music-${req.id}`}>
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => updateStatus(req.id, 'rejected')} className="p-1.5 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400" title="Afvis" data-testid={`reject-music-${req.id}`}>
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <button onClick={() => updateStatus(req.id, 'queued')} className="p-1.5 rounded hover:bg-blue-500/10 text-slate-400 hover:text-blue-400" title="Sæt i kø" data-testid={`queue-music-${req.id}`}>
                            <Clock className="w-4 h-4" />
                          </button>
                        )}
                        {req.status === 'queued' && (
                          <button onClick={() => updateStatus(req.id, 'played')} className="p-1.5 rounded hover:bg-purple-500/10 text-slate-400 hover:text-purple-400" title="Markér som spillet" data-testid={`played-music-${req.id}`}>
                            <Music className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(event) => handleDeleteClick(req, event)}
                          disabled={deletingIds.has(getRequestId(req))}
                          className="p-1.5 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400 disabled:opacity-50"
                          title="Slet"
                          aria-label="Slet musikønske"
                          data-testid={`remove-music-${getRequestId(req)}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <p className="text-center py-8 text-slate-500 text-sm">Ingen musikønsker fundet.</p>}
        </div>
      )}

      <Dialog open={!!showSpotify} onOpenChange={() => { setShowSpotify(null); setSearchResults([]); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Disc3 className="w-5 h-5 text-emerald-400" /> Find på Spotify
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="spotify-search-form">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSpotifySearch()}
              placeholder="Søg efter sang eller kunstner..."
              className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm"
              data-testid="spotify-search-input" />
            <Button onClick={handleSpotifySearch} disabled={searching} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="spotify-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="spotify-search-results">
            {searching && <p className="text-sm text-slate-400 text-center py-4">Søger...</p>}
            {!searching && searchResults.length === 0 && searchQuery && <p className="text-sm text-slate-500 text-center py-4">Ingen resultater. Prøv en anden søgning.</p>}
            {searchResults.map(track => (
              <button key={track.id} onClick={() => handleLinkTrack(showSpotify, track)}
                className="w-full flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-left"
                data-testid={`spotify-track-${track.id}`}>
                {track.album_art ? (
                  <img src={track.album_art} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Disc3 className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 truncate">{track.name}</p>
                  <p className="text-xs text-slate-400 truncate">{track.artist}</p>
                  <p className="text-xs text-slate-600 truncate">{track.album} {formatDuration(track.duration_ms)}</p>
                </div>
                <Link2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
